import { useEffect, useMemo, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm } from "react-hook-form";
import { IDropdownModel } from "common/models/IDropdownModel";
import DatePickerElement from "components/DatePickerElement";
import {
  IProgramCirculation,
  IProgramCirculationGridFilter,
  IProgramCirculationGridFilterSchema,
} from "../api/types";
import dayjs from "dayjs";
import { GetProgramValues } from "features/program/api/hooks";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import useDashboardStore from "../store/DashboardStore";
import { GetProgramCirculation } from "../api/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectField } from "components/@extended/SelectField";
import StyledDataGrid from "components/@extended/StyledDataGrid";

const ProgramCirGrid = () => {
  const {
    programCirculationPostModel,
    handleProgramCirculationFiltering,
    handleProgramCirculationPagination,
    handleProgramCirculationSorting,
  } = useDashboardStore();

  const { data: getProgramCirculations, isFetching } = GetProgramCirculation(
    programCirculationPostModel,
  );

  const columns: GridColDef<IProgramCirculation>[] = useMemo(
    () => [
      {
        field: "materialTypeName",
        headerName: "Material Type",
        flex: 1,
      },
      {
        field: "today",
        headerName: "Today",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <>
            <span
              style={{ color: "red", cursor: "pointer" }}
              title="Today Issued Count"
            >
              {params.row?.todayCount}
            </span>
            <span> | </span>
            <span
              style={{ color: "green", cursor: "pointer" }}
              title="Today Return Count"
            >
              {params.row?.todayReturnCount}
            </span>
          </>
        ),
      },
      {
        field: "lastWeek",
        headerName: "Last Week",
        headerAlign: "center",
        flex: 1,
        align: "center",
        renderCell: (params) => (
          <>
            <span
              style={{ color: "red", cursor: "pointer" }}
              title="Last Week Issued Count"
            >
              {params.row.lastWeekCount}
            </span>
            <span> | </span>
            <span
              style={{ color: "green", cursor: "pointer" }}
              title="Last Week Return Count"
            >
              {params.row.lastWeekReturnCount}
            </span>
          </>
        ),
      },
      {
        field: "last30Days",
        headerName: "Last 30 Days",
        headerAlign: "center",
        flex: 1,
        align: "center",
        renderCell: (params) => (
          <>
            <span
              style={{ color: "red", cursor: "pointer" }}
              title="Last Month Issued Count"
            >
              {params.row.last30DaysCount}
            </span>
            <span> | </span>
            <span
              style={{ color: "green", cursor: "pointer" }}
              title="Last Month Return Count"
            >
              {params.row.last30DaysReturnCount}
            </span>
          </>
        ),
      },
      {
        field: "filterDate",
        // headerName: `${sixMonthsAgo} - ${today}`,
        headerName: "From Date  -  To Date",
        headerAlign: "center",
        flex: 1,
        align: "center",
        renderCell: (params) => (
          <>
            <span
              style={{ color: "red", cursor: "pointer" }}
              title="Filtered Date Issued Count"
            >
              {params.row.filterDateCount}
            </span>
            <span> | </span>
            <span
              style={{ color: "green", cursor: "pointer" }}
              title="Filtered Date Return Count"
            >
              {params.row.filterDateReturnCount}
            </span>
          </>
        ),
      },
    ],
    [],
  );

  const methods = useForm<IProgramCirculationGridFilter>({
    mode: "all",
    resolver: zodResolver(IProgramCirculationGridFilterSchema),
  });


  const { reset, handleSubmit, control, watch, setValue } = methods;

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  useEffect(() => {
    if (fromDate && toDate) {
      const from = dayjs(fromDate);
      const to = dayjs(toDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        setValue("toDate", fromDate);
      }
    }
  }, [fromDate, toDate, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    handleProgramCirculationFiltering(data);
  });

  const handleClear = () => {
    handleProgramCirculationFiltering({
      programID: null,
      memberTypeID: null,
      fromDate: dayjs().subtract(6, "month").toString(),
      toDate: dayjs().toString(),
    });
  };

  useEffect(() => {
    reset(programCirculationPostModel?.filterModel);
  }, [programCirculationPostModel?.filterModel, reset]);

  const rowCountRef = useRef(getProgramCirculations?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (getProgramCirculations?.apiData?.count !== undefined) {
      rowCountRef.current = getProgramCirculations?.apiData?.count;
    }
    return rowCountRef.current;
  }, [getProgramCirculations?.apiData?.count]);

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: programQuery } = GetProgramValues();
  const [programOptions, setProgramOptions] = useState<IDropdownModel[]>([]);
  useEffect(() => {
    setProgramOptions(programQuery?.apiData || []);
  }, [programQuery]);

  return (
    <>
      {/* <LovelyGridProvider initFilter={initFilter}>
        <FilterContainer
          clearForm={reset}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
        >
          <Typography variant="h3" component={"h3"} marginBlockEnd={2}>
            Program Wise Circulation
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6} md={4} lg={3}>
              <DatePickerElement
                control={control}
                name="fromDate"
                label="From Date"
                format="DD-MM-YYYY"
                // defaultValue={dayjs(sixMonthsAgo, "DD-MM-YYYY")}
                sx={{ width: "100%" }}
              ></DatePickerElement>
            </Grid>

            <Grid item xs={6} md={4} lg={3}>
              <DatePickerElement
                control={control}
                name="toDate"
                label="To Date"
                format="DD-MM-YYYY"
                // defaultValue={dayjs(today, "DD-MM-YYYY")}
                sx={{ width: "100%" }}
              ></DatePickerElement>
            </Grid>

            <Grid item xs={6} md={4} lg={3}>
              <AutoCompleteElement
                options={memberTypeOptions}
                control={control}
                name="memberTypeID"
                label="Member Type"
                placeholder="Select Member Type"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <AutoCompleteElement
                options={programOptions}
                control={control}
                name="programID"
                label="Program"
                placeholder="Select Program"
              />
            </Grid>
          </Grid>
        </FilterContainer>

        <Paper elevation={3} sx={{ mt: 2, px: 2, py: 2 }}>
          <Grid sx={{ display: "flex", justifyContent: "end", mb: 3 }}>
            <Grid
              justifyContent={"end"}
              container
              alignItems="center"
              maxWidth={"150px"}
            >
              <Box
                sx={{
                  backgroundColor: "red",
                  padding: "4px",
                  color: "white",
                  borderRadius: "30%",
                  textAlign: "center",
                }}
              ></Box>
              <Typography color={"red"} sx={{ paddingLeft: "3px" }}>
                - Issued items
              </Typography>
            </Grid>
            <Grid
              justifyContent={"end"}
              container
              alignItems="center"
              maxWidth={"150px"}
            >
              <Box
                sx={{
                  backgroundColor: "green",
                  padding: "4px",
                  color: "white",
                  borderRadius: "30%",
                  textAlign: "center",
                }}
              ></Box>
              <Typography color={"green"} sx={{ paddingLeft: "3px" }}>
                - Return items
              </Typography>
            </Grid>
          </Grid>
          <Grid sx={{ height: 400, width: "100%" }}>
            <LovelyGrid
              queryKey="programCirculation"
              columns={columns}
              fetcher={request.getProgramCirculation}
              id={(item) => item.uniqueID}
              modalFetcher={request.getViewById}
              modalElement={View}
            ></LovelyGrid>
          </Grid>
        </Paper>
      </LovelyGridProvider> */}

      <Box component="form" onSubmit={onSubmit}>
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h3" component={"h3"} marginBlockEnd={2}>
              Circulations
              <Divider sx={{ my: 1 }} />
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <DatePickerElement
                  control={control}
                  name="fromDate"
                  label="From Date"
                  format="DD-MM-YYYY"
                  maxDate={
                    toDate && dayjs(toDate).isValid()
                      ? dayjs(toDate)
                      : undefined
                  }
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <DatePickerElement
                  control={control}
                  name="toDate"
                  label="To Date"
                  format="DD-MM-YYYY"
                  minDate={
                    fromDate && dayjs(fromDate).isValid()
                      ? dayjs(fromDate)
                      : undefined
                  }
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={memberTypeOptions}
                  control={control}
                  name="memberTypeID"
                  label="Member Type"
                  placeholder="Select Member Type"
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={programOptions}
                  control={control}
                  name="programID"
                  label="Program"
                  placeholder="Select Program"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3} sx={{ mt: 3 }}>
                <LoadingButton
                  variant="contained"
                  sx={{ mr: 1 }}
                  type="submit"
                  loading={isFetching}
                  disabled={isFetching}
                >
                  Search
                </LoadingButton>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClear}
                  disabled={isFetching}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Grid container direction={"row"} spacing={2}>
        <Grid item xs={12} md={12}>
          <Card elevation={3} sx={{ p: 3 }}>
            <Grid sx={{ display: "flex", justifyContent: "end", mb: 3 }}>
              <Grid
                justifyContent={"end"}
                container
                alignItems="center"
                maxWidth={"150px"}
              >
                <Box
                  sx={{
                    backgroundColor: "red",
                    padding: "4px",
                    color: "white",
                    borderRadius: "30%",
                    textAlign: "center",
                  }}
                ></Box>
                <Typography color={"red"} sx={{ paddingLeft: "3px" }}>
                  - Issued items
                </Typography>
              </Grid>
              <Grid
                justifyContent={"end"}
                container
                alignItems="center"
                maxWidth={"150px"}
              >
                <Box
                  sx={{
                    backgroundColor: "green",
                    padding: "4px",
                    color: "white",
                    borderRadius: "30%",
                    textAlign: "center",
                  }}
                ></Box>
                <Typography color={"green"} sx={{ paddingLeft: "3px" }}>
                  - Return items
                </Typography>
              </Grid>
            </Grid>
            <Grid container sx={{ height: 450 }}>
              <StyledDataGrid
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "wrap",
                    fontWeight: 600,
                    lineHeight: "normal",
                    fontSize: "13px",
                  },
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "wrap",
                    alignContent: "center",
                    fontSize: 14,
                  },
                }}
                density="compact"
                disableRowSelectionOnClick
                getRowId={(item) => item.uniqueID}
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      ...programCirculationPostModel.pagingModel,
                    },
                  },
                }}
                sortModel={programCirculationPostModel.sortModel}
                onSortModelChange={handleProgramCirculationSorting}
                onPaginationModelChange={handleProgramCirculationPagination}
                getRowHeight={() => "auto"}
                paginationMode="server"
                sortingMode="server"
                rows={getProgramCirculations?.apiData?.data}
                rowCount={rowCount}
                disableColumnFilter
                disableColumnSorting
                loading={isFetching}
              />
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default ProgramCirGrid;
